import { useEffect, useRef } from 'react';
import { resolve } from './tokens.js';

/*
 * Composer
 *
 * The input.
 *
 * Enter sends, Shift and Enter makes a new line. That is what people expect
 * from a chat and the opposite of what they expect from a form, which is why it
 * is worth being deliberate: somebody describing a fault will write two or
 * three lines, and a box that submits on the first Return loses the rest of
 * their sentence.
 *
 * The field grows with its content up to a ceiling and then scrolls, so a long
 * description stays readable while it is being written without pushing the
 * conversation off the screen.
 *
 * No attachments. Not unimplemented, but out of scope by decision, so there is
 * no paperclip to promise one.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const MAX_HEIGHT = 140;
const Composer = ({
  busy,
  disabled,
  onChange,
  onSend,
  tokens: raw,
  value
}) => {
  const field = useRef(null);
  const tokens = resolve(raw);
  const canSend = !busy && !disabled && value.trim().length > 0;

  // Grow to fit, then scroll. Reset first so it can shrink again on delete.
  useEffect(() => {
    let node = field.current;
    if (node) {
      node.style.height = 'auto';
      node.style.height = `${Math.min(node.scrollHeight, MAX_HEIGHT)}px`;
    }
  }, [value]);
  const onKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) {
        onSend();
      }
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    style: {
      alignItems: 'flex-end',
      backgroundColor: tokens.background,
      border: `1px solid ${tokens.border}`,
      borderRadius: 22,
      display: 'flex',
      gap: 8,
      padding: '8px 8px 8px 16px',
      width: '100%'
    },
    children: [/*#__PURE__*/_jsx("textarea", {
      "aria-label": 'Message GRACI',
      disabled: disabled,
      onChange: event => onChange(event.target.value),
      onKeyDown: onKeyDown,
      placeholder: disabled ? 'Reconnecting...' : 'Tell GRACI what is happening',
      ref: field,
      rows: 1,
      style: {
        backgroundColor: 'transparent',
        border: 'none',
        color: tokens.text,
        flexGrow: 1,
        fontFamily: tokens.font,
        fontSize: 15,
        lineHeight: 1.5,
        maxHeight: MAX_HEIGHT,
        outline: 'none',
        padding: '7px 0',
        resize: 'none'
      },
      value: value
    }), /*#__PURE__*/_jsx("button", {
      "aria-label": 'Send message',
      disabled: !canSend,
      onClick: canSend ? onSend : null,
      style: {
        alignItems: 'center',
        backgroundColor: canSend ? tokens.accent : tokens.subdued,
        border: 'none',
        borderRadius: 999,
        cursor: canSend ? 'pointer' : 'default',
        display: 'inline-flex',
        flexShrink: 0,
        height: 34,
        justifyContent: 'center',
        transition: 'background-color 0.2s ease',
        width: 34
      },
      children: /*#__PURE__*/_jsx("svg", {
        fill: 'none',
        height: 15,
        stroke: canSend ? tokens.onAccent : tokens.muted,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: 2.2,
        viewBox: '0 0 24 24',
        width: 15,
        children: /*#__PURE__*/_jsx("path", {
          d: 'M12 19V5M5 12l7-7 7 7'
        })
      })
    })]
  });
};
export default Composer;